// src/components/common/ThoughtAndMemoryPanel.jsx
import React, { useRef, useState, useEffect } from 'react';
import { Brain, Sparkles, AlertTriangle, ChevronUp, ChevronDown, Expand, Trash2 } from 'lucide-react';
import KittScanner from './KittScanner';
import ThoughtStream from './ThoughtStream';
import { authManager } from '@/managers/AuthManager';
import { platformCannon } from '@/managers/ChatFeedManager/PlatformCannon';

/* ========================
 * MemoryItem - Renders a single memory with a delete button.
 * ========================*/
const MemoryItem = ({ memory, isDarkTheme, onDelete, isLoadingOrStreaming }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleDelete = async () => {
    if (isDeleting || !memory.memoryId) return;
    setIsDeleting(true);

    const user = authManager.getCurrentUser();
    if (!user) {
      console.error("User not authenticated for memory deletion.");
      setIsDeleting(false);
      return;
    }

    try {
      // ARCHITECTURAL FIX: Use PlatformCannon to call the correct backend proxy endpoint.
      // This solves the 404 Not Found error by targeting '/memory/delete',
      // which is defined in the backend router. The backend handles the house API key.
      const result = await platformCannon.invokeService('/memory/delete', {
        userId: user.uid,
        memoryId: memory.memoryId,
      });

      if (!result.success) {
        // PlatformCannon provides a standardized error object
        throw new Error(result.error.message || 'Failed to delete memory. The server returned an error.');
      }
      
      onDelete(memory.memoryId);
    } catch (error) {
      console.error("Error deleting memory:", error);
      setIsDeleting(false);
      setIsConfirming(false); // Reset state on error to allow user retry
    }
  };
  
  const handleStartConfirm = (e) => {
    e.stopPropagation();
    setIsConfirming(true);
  };

  const handleCancelConfirm = (e) => {
    e.stopPropagation();
    setIsConfirming(false);
  };


  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative flex items-start justify-between gap-2 p-1 rounded-md transition-colors duration-200 ${
        isDarkTheme ? 'hover:bg-white/5' : 'hover:bg-black/5'
      } ${isDeleting ? 'opacity-50' : ''}`}
    >
      <p className="flex-1 text-xs leading-relaxed italic text-gray-400">
        - {memory.content.replace(/^- /, '').trim()}
      </p>
      {memory.memoryId && (
        <div className="flex-shrink-0 h-[20px] flex items-center"> {/* Ensures consistent height */}
          {isConfirming ? (
            <div className="flex items-center gap-1.5" aria-live="polite">
              <span className="text-xs text-gray-400">Delete?</span>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-1.5 py-0.5 rounded text-xs transition-colors font-semibold text-red-400 hover:bg-red-500/20 disabled:opacity-50 disabled:cursor-wait"
                title="Confirm deletion"
              >
                {isDeleting ? '...' : 'Confirm'}
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={isDeleting}
                className="px-1.5 py-0.5 rounded text-xs text-gray-500 hover:text-gray-300 hover:bg-white/10 disabled:opacity-50"
                title="Cancel"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={handleStartConfirm}
              disabled={isLoadingOrStreaming}
              className={`p-1 rounded transition-opacity text-gray-500 hover:text-red-400 focus:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed ${
                isHovered ? 'opacity-100' : 'opacity-0'
              } ${isDarkTheme ? 'hover:bg-red-500/10' : 'hover:bg-red-500/10'}`}
              title={isLoadingOrStreaming ? "Cannot delete memories while streaming" : "Delete this memory"}
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};


/* ========================
 * ThoughtAndMemoryPanel - Displays thinking process with optional memories tab.
 * Renders immediately and fades in to prevent parent component jank.
 * ========================*/
const ThoughtAndMemoryPanel = ({
  thoughtSummary,
  hasThinkingBlock,
  hasSurfacedMemories,
  retrievedMemories,
  isLoadingOrStreaming,
  memoriesHadError,
  isCollapsed,
  onToggleCollapse,
  isDarkTheme,
}) => {
  const thinkingContentRef = useRef(null);
  const memoriesContentRef = useRef(null);
  const [activeTab, setActiveTab] = useState('thinking');
  const [userHasChosenTab, setUserHasChosenTab] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [localMemories, setLocalMemories] = useState([]);

  useEffect(() => {
    setLocalMemories(retrievedMemories || []);
  }, [retrievedMemories]);

  const showMemoriesTab = hasSurfacedMemories || memoriesHadError;

  useEffect(() => {
    if (userHasChosenTab) return;
    if (hasThinkingBlock) {
      setActiveTab('thinking');
    } else if (showMemoriesTab) {
      setActiveTab('memories');
    }
  }, [hasThinkingBlock, showMemoriesTab, userHasChosenTab]);

  const handleMemoryDelete = (memoryIdToDelete) => {
    setLocalMemories(currentMemories =>
      currentMemories.filter(mem => mem.memoryId !== memoryIdToDelete)
    );
  };
  
  /* ========================
   * getMemoryContent - Renders the list of MemoryItem components.
   * ========================*/
  const getMemoryContent = () => {
    if (isLoadingOrStreaming && localMemories.length === 0 && !memoriesHadError) return null; // Wait for first delta
    if (memoriesHadError) return <div className="p-1.5">Could not retrieve memories due to an error.</div>;
    
    if (hasSurfacedMemories && localMemories.length > 0) {
      return localMemories.map((mem, index) => (
        <MemoryItem
          key={mem.memoryId || `mem-${index}`}
          memory={mem}
          isDarkTheme={isDarkTheme}
          onDelete={handleMemoryDelete}
          isLoadingOrStreaming={isLoadingOrStreaming}
        />
      ));
    }
    
    if (hasSurfacedMemories && localMemories.length === 0) {
      return <div className="p-1.5">All surfaced memories for this turn have been deleted.</div>;
    }
    
    if (!isLoadingOrStreaming && localMemories.length === 0) {
       return <div className="p-1.5">No specific memories were surfaced for this turn.</div>;
    }

    return null;
  };

  const handleToggleExpand = () => setIsExpanded(!isExpanded);
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setUserHasChosenTab(true);
  };

  const baseContainerClasses = `p-2 mb-2 rounded-lg border overflow-hidden transition-colors duration-300 ${
    isDarkTheme ? 'bg-black/20 border-white/10' : 'bg-background-dark border-border-color'
  }`;
  const streamingContainerClasses = isLoadingOrStreaming ? 'animate-pulse-border-glow' : '';
  const contentHeightClass = isExpanded ? 'max-h-[300px]' : 'max-h-[130px]';
  const shouldAutoScroll = isLoadingOrStreaming;

  const contentTextClasses = `text-xs leading-relaxed italic markdown-content-thoughts whitespace-pre-line break-words ${
    isDarkTheme ? 'text-gray-400' : 'text-on-surface opacity-90'
  }`;
  
  return (
    <div className={`${baseContainerClasses} ${streamingContainerClasses} animate-fade-in`}>
      <div className={`flex items-center justify-between gap-1 ${!isCollapsed ? 'mb-2' : ''}`}>
        <div className="flex items-center gap-1">
          {hasThinkingBlock && (
            <button
              onClick={() => handleTabChange('thinking')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors font-medium ${
                activeTab === 'thinking'
                  ? (isDarkTheme ? 'bg-sky-400/20 text-sky-300' : 'bg-primary/10 text-primary-dark border border-primary/20')
                  : (isDarkTheme ? 'text-gray-400 hover:bg-white/5 hover:text-gray-300' : 'text-on-surface-dim hover:bg-black/5 hover:text-on-surface')
              }`}
            >
              <Brain size={12} className={activeTab === 'thinking' ? (isDarkTheme ? 'text-sky-400' : 'text-primary-dark') : 'text-on-surface-dim'} />
              <span>Thinking</span>
            </button>
          )}
          
          {showMemoriesTab && (
            <button
              onClick={() => handleTabChange('memories')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors font-medium ${
                activeTab === 'memories'
                  ? (isDarkTheme ? 'bg-sky-400/20 text-sky-300' : 'bg-primary/10 text-primary-dark border border-primary/20')
                  : (isDarkTheme ? 'text-gray-400 hover:bg-white/5 hover:text-gray-300' : 'text-on-surface-dim hover:bg-black/5 hover:text-on-surface')
              }`}
            >
              {memoriesHadError ? (
                <AlertTriangle size={12} className="text-error" />
              ) : (
                <Sparkles size={12} className={activeTab === 'memories' ? (isDarkTheme ? 'text-sky-400' : 'text-primary-dark') : 'text-on-surface-dim'} />
              )}
              <span>Memories</span>
            </button>
          )}
        </div>
        
        <button
          onClick={onToggleCollapse}
          className={`p-1 rounded-full transition-colors ${isDarkTheme ? 'text-gray-400 hover:bg-white/10 hover:text-gray-200' : 'text-on-surface-dim hover:bg-black/10 hover:text-on-surface'}`}
          aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
        >
          {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </button>
      </div>

      {!isCollapsed && (
        <div className="relative">
          <div className={activeTab === 'thinking' && hasThinkingBlock ? 'block' : 'hidden'}>
            <KittScanner isAnimating={isLoadingOrStreaming} />
            <div
              ref={thinkingContentRef}
              className={`${contentHeightClass} overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-primary/20 hover:scrollbar-thumb-primary/30 transition-all duration-300`}
            >
              <ThoughtStream
                text={thoughtSummary}
                scrollRef={shouldAutoScroll ? thinkingContentRef : null}
                className={contentTextClasses}
                isDarkTheme={isDarkTheme}
              />
            </div>
          </div>
          
          <div className={activeTab === 'memories' ? 'block' : 'hidden'}>
            <div
              ref={memoriesContentRef}
              className={`${contentHeightClass} overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-primary/20 hover:scrollbar-thumb-primary/30 transition-all duration-300`}
            >
              <div className="flex flex-col gap-0.5">
                {getMemoryContent()}
              </div>
            </div>
          </div>
          
          <button
            onClick={handleToggleExpand}
            className={`absolute bottom-1 right-1 p-1 rounded-full transition-colors opacity-60 hover:opacity-100 ${isDarkTheme ? 'text-gray-500 hover:text-gray-300 hover:bg-white/10' : 'text-on-surface-dim/70 hover:text-on-surface-dim hover:bg-black/10'}`}
            aria-label={isExpanded ? 'Contract panel' : 'Expand panel'}
            title={isExpanded ? 'Contract panel' : 'Expand panel'}
          >
            <Expand 
              size={12} 
              className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default ThoughtAndMemoryPanel;